import * as React from "react";

import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import Button from "@/components/button";

export type MultiSelectOption = string | { label: string; value: string };
export type MultiSelectProps = {
  options: Array<MultiSelectOption>;
  value: Array<string>;
  onChange: (next: Array<string>) => void;

  label?: string;
  placeholder?: string;
  primaryFromFirst?: boolean;
  disabled?: boolean;
  maxSelected?: number;
  allowCustom?: boolean;
  className?: string;

  normalize?: (s: string) => string;
};

export function MultiSelect({
  options,
  value,
  onChange,
  label,
  placeholder,
  primaryFromFirst = true,
  disabled,
  maxSelected,
  allowCustom = true,
  className,
  normalize = (s: string) => s.trim().toLowerCase(),
}: MultiSelectProps) {
  const { t } = useTranslation("common");
  const resolvedLabel = label ?? t("multiSelect.defaultLabel");
  const resolvedPlaceholder = placeholder ?? t("multiSelect.defaultPlaceholder");
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [highlight, setHighlight] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const canAddMore = maxSelected == null || value.length < maxSelected;
  const selectedSet = React.useMemo(
    () => new Set(value.map(normalize)),
    [value, normalize],
  );

  const normalizedOptions = React.useMemo(
    () =>
      options.map((o) => (typeof o === "string" ? { label: o, value: o } : o)),
    [options],
  );

  const filtered = React.useMemo(() => {
    const q = normalize(query);
    const base = normalizedOptions
      .filter((o) => normalize(o.label).includes(q))
      .filter((o) => !selectedSet.has(normalize(o.value)));
    if (
      allowCustom &&
      q &&
      !selectedSet.has(q) &&
      !base.some((b) => normalize(b.value) === q)
    ) {
      const existing = normalizedOptions.find((o) => normalize(o.value) === q);
      if (existing) {
        return [existing, ...base];
      }
      return [{ label: query, value: query }, ...base];
    }
    return base;
  }, [normalizedOptions, query, selectedSet, normalize, allowCustom]);

  const primary = primaryFromFirst ? (value[0] ?? "") : "";

  function addItem(s: string) {
    if (!canAddMore) return;
    const trimmed = s.trim();
    if (!trimmed) return;
    const n = normalize(trimmed);
    if (selectedSet.has(n)) return;
    onChange([...value, trimmed]);
    setQuery("");
    setHighlight(0);
    if (inputRef.current) inputRef.current.value = "";
    setOpen(false);
  }

  function removeItem(s: string) {
    onChange(value.filter((v) => v !== s));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length > 0)
        addItem(filtered[Math.min(highlight, filtered.length - 1)].value);
      else if (allowCustom) addItem(query);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      setHighlight(0);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    }
  }

  return (
    <div className={["w-full", className].filter(Boolean).join(" ")}>
      {/* Header */}
      <div className="flex items-center justify-between rounded-xl bg-fog px-4 py-3">
        <div className="flex flex-col">
          <span className="body-small text-garnet/80">{resolvedLabel}</span>
          {primaryFromFirst && (
            <span className="body font-semibold">{primary || "—"}</span>
          )}
        </div>

        {/* Keep this button OUTSIDE a disabled fieldset if it must always work */}
        <Button
          type="button"
          size="icon"
          variant="secondary"
          onClick={() => setOpen((s) => !s)}
          disabled={disabled || !canAddMore}
          className="h-8 w-8 rounded-full bg-cocoa text-pm-white hover:bg-cocoa/90 disabled:opacity-50"
          aria-label={t("multiSelect.addItem")}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Inline input + suggestions */}
      {open && !disabled && canAddMore && (
        <div className="mt-3">
          <div className="relative">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              autoFocus
              placeholder={resolvedPlaceholder}
              className="
                w-full rounded-md border border-black/10
                bg-white text-garnet placeholder:text-garnet/50
                focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#B8860B]
                px-3 py-2
              "
            />
            {filtered.length > 0 && (
              <ul
                role="listbox"
                className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-black/10 bg-white shadow"
              >
                {filtered.slice(0, 8).map((s, i) => {
                  return (
                    <li
                      key={`${s}-${i}`}
                      role="option"
                      aria-selected={i === highlight}
                      onClick={(e) => {
                        e.preventDefault();
                        addItem(s.value);
                        setTimeout(() => {
                          setQuery("");
                          if (inputRef.current) inputRef.current.value = "";
                        }, 0);
                      }}
                      onMouseEnter={() => setHighlight(i)}
                      className={[
                        "cursor-pointer px-3 py-2 text-sm",
                        i === highlight ? "bg-fog" : "hover:bg-fog",
                      ].join(" ")}
                    >
                      {s.label}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Chips — now include ALL items, primary included */}
      {value.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {value.map((item, idx) => {
            const isPrimary = primaryFromFirst && idx === 0;
            const found = normalizedOptions.find((o) => o.value === item);
            return (
              <span
                key={`${item}-${idx}`}
                className={[
                  "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm",
                  isPrimary
                    ? "bg-gradient-to-r from-[#B8860B] to-[#E3B23C] text-white font-semibold"
                    : "bg-[#FBF4EA] text-garnet",
                ].join(" ")}
              >
                {found ? found.label : item}
                <button
                  type="button"
                  onClick={() => removeItem(item)}
                  disabled={!!disabled}
                  className={[
                    "inline-flex h-5 w-5 items-center justify-center rounded-full border border-black/20 text-xs hover:bg-black/5 disabled:opacity-50",
                    isPrimary ? "border-white/50 hover:bg-white/10" : "",
                  ].join(" ")}
                  aria-label={t(isPrimary ? "multiSelect.removePrimary" : "multiSelect.remove", { name: found ? found.label : item })}
                  title={t(isPrimary ? "multiSelect.removePrimary" : "multiSelect.remove", { name: found ? found.label : item })}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
